import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  StarIcon,
  MapPinIcon,
  ShareIcon,
  ChatBubbleLeftIcon,
  HeartIcon,
  ChevronLeftIcon,
  PhoneIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid, HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { FaWhatsapp, FaInstagram } from 'react-icons/fa';
import PageContainer from '../components/layout/PageContainer';
import RateModal from '../components/RateModal';
import BusinessEditModal from '../components/BusinessEditModal';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Lottie from 'lottie-react';
import starsAnimation from '../assets/Stars.json';
import winSound from '../assets/Inspiring Win.wav';
import whatsappIcon from '../assets/whatsapp.svg';
import instagramIcon from '../assets/instagram.svg';
import SessionExpiredModal from '../components/SessionExpiredModal';
import ShareModal from '../components/ShareModal';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';
import { Dialog } from '@headlessui/react';

// Add image cache
const imageCache: { [key: string]: HTMLImageElement } = {};
const fallbackCache: { [key: string]: HTMLImageElement } = {};

// Preload image function
const preloadImage = (src: string, category: string): Promise<void> => {
  // Check both caches first
  if (imageCache[src]) {
    return Promise.resolve();
  }
  if (fallbackCache[category]) {
    imageCache[src] = fallbackCache[category];
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      imageCache[src] = img;
      resolve();
    };
    img.onerror = () => {
      // If original image fails, try fallback
      const fallbackSrc = `https://getimage-b7jrylvg3a-uc.a.run.app/categories/${category}.png`;
      
      // Check if fallback is already cached
      if (fallbackCache[category]) {
        imageCache[src] = fallbackCache[category];
        resolve();
        return;
      }

      const fallbackImg = new Image();
      fallbackImg.onload = () => {
        fallbackCache[category] = fallbackImg;
        imageCache[src] = fallbackImg;
        resolve();
      };
      fallbackImg.onerror = () => {
        // If fallback also fails, use a default image
        const defaultImg = new Image();
        defaultImg.onload = () => {
          fallbackCache[category] = defaultImg;
          imageCache[src] = defaultImg;
          resolve();
        };
        defaultImg.onerror = reject;
        defaultImg.src = 'https://getimage-b7jrylvg3a-uc.a.run.app/categories/default.png';
      };
      fallbackImg.src = fallbackSrc;
    };
    img.src = src;
  });
};

// Add shimmer effect component with animation
const ShimmerEffect = () => (
  <div className="relative w-full h-full overflow-hidden">
    <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-white/5 via-white/10 to-white/5 bg-[length:200%_100%]" />
  </div>
);

interface Business {
  id: string;
  name: string;
  image: string;
  rating: number;
  reviews: number;
  description: string;
  address: string;
  category: string;
  phone: string;
  premiumType?: 'gold' | 'silver' | 'bronze';
  accessLevel?: number;
  instagram?: string;
}

const WhatsAppIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const BusinessDetail = () => {
  const { id } = useParams();
  const { user, refreshToken, logout } = useAuth();
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRateModalOpen, setIsRateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [loadedImages, setLoadedImages] = useState<{ [key: string]: boolean }>({});
  const [isSessionExpiredModalOpen, setIsSessionExpiredModalOpen] = useState(false);
  const [isPhotoUploadModalOpen, setIsPhotoUploadModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(winSound);
    audioRef.current.volume = 0.5; // Set volume to 50%
  }, []);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Add function to check if business is favorited
  const checkIfFavorited = async () => {
    if (!user?.token) return;
    
    try {
      let token = user.token;
      console.log('JWT Token:', {
        token: token,
        length: token.length,
        firstChars: token.substring(0, 10) + '...',
        lastChars: '...' + token.substring(token.length - 10)
      });
      
      let response = await fetch('https://us-central1-appdizquetem.cloudfunctions.net/listFavorites', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // If token expired, refresh and retry
      if (response.status === 401) {
        const newToken = await refreshToken();
        if (!newToken) {
          throw new Error('Failed to refresh token');
        }
        
        response = await fetch('https://us-central1-appdizquetem.cloudfunctions.net/listFavorites', {
          headers: {
            'Authorization': `Bearer ${newToken}`
          }
        });
      }
      
      if (!response.ok) throw new Error('Failed to fetch favorites');
      
      const favorites = await response.json();
      
      // Verificar se o business existe e se há correspondência nos favoritos
      const isFavorited = business?.id && favorites.some((fav: any) => fav.id === business.id);
      
      setIsFavorite(!!isFavorited);
    } catch (error) {
      console.error('Error checking favorites:', error);
    }
  };

  useEffect(() => {
    const fetchBusiness = async () => {
      try {
        const response = await fetch(`https://getbusinessdetail-b7jrylvg3a-uc.a.run.app?id=${id}`);
        const businessData = await response.json();
        
        // Debug logs
        console.log('User data:', {
          uid: user?.uid,
          accessLevel: user?.accessLevel,
          token: user?.token ? 'present' : 'missing'
        });
        console.log('Business data:', businessData);
        
        // Transform the API response to match our Business interface
        const transformedBusiness: Business = {
          id: businessData.id,
          name: businessData.name,
          image: businessData.image,
          rating: businessData.stars,
          reviews: businessData.reviews,
          description: businessData.description,
          address: businessData.address,
          category: businessData.category,
          phone: businessData.phone,
          premiumType: businessData.premiumType,
          accessLevel: businessData.accessLevel,
          instagram: businessData.instagram,
        };
        
        setBusiness(transformedBusiness);

        // Preload the business image
        if (transformedBusiness.image && transformedBusiness.category) {
          preloadImage(transformedBusiness.image, transformedBusiness.category)
            .then(() => setImagesLoaded(true))
            .catch(error => {
              console.error('Error preloading image:', error);
              setImagesLoaded(true); // Still set to true to show content
            });
        }
      } catch (error) {
        console.error('Error loading business:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBusiness();
  }, [id, user]);

  // Novo useEffect para verificar favoritos quando o business ou user.token mudar
  useEffect(() => {
    if (business && user?.token) {
      checkIfFavorited();
    }
  }, [business, user?.token]);

  const handleImageLoad = (imageUrl: string) => {
    setLoadedImages(prev => ({
      ...prev,
      [imageUrl]: true
    }));
  };

  const handleSessionExpired = () => {
    setIsSessionExpiredModalOpen(true);
    logout();
  };

  const handlePhotoUploadComplete = (imageUrl: string) => {
    if (business) {
      setBusiness({
        ...business,
        image: imageUrl
      });
    }
  };

  const handleShare = async () => {
    try {
      const url = window.location.href;
      await navigator.clipboard.writeText(url);
      setIsShareModalOpen(true);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  const handleImageUpload = async (file: File) => {
    if (!user?.token) {
      handleSessionExpired();
      return;
    }

    // Validar tipo de arquivo
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Tipo de arquivo não permitido. Use apenas imagens (JPEG, PNG, GIF, WEBP)');
      return;
    }

    // Validar tamanho (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError('Arquivo muito grande. Tamanho máximo: 5MB');
      return;
    }

    try {
      setUploadError(null);
      setUploadProgress(0);

      // Criar preview da imagem
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Converter imagem para PNG
      const img = new Image();
      img.src = URL.createObjectURL(file);
      
      await new Promise((resolve) => {
        img.onload = resolve;
      });

      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0);
      
      // Converter canvas para PNG
      const pngBlob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
        }, 'image/png');
      });

      // Criar nome do arquivo usando o businessId
      const filePath = `images/businesses/${business?.id}.png`;

      // Criar referência no Storage
      const storageRef = ref(storage, filePath);

      // Definir metadados com o token JWT
      const metadata = {
        contentType: 'image/png',
        customMetadata: {
          businessId: business?.id || '',
          uploadedBy: user.uid,
          uploadedAt: Date.now().toString(),
          authorization: `Bearer ${user.token}` // Adicionando o token JWT nos metadados
        },
      };
      
      console.log('Metadata:', metadata);

      // Upload do arquivo com autenticação
      const result = await uploadBytes(storageRef, pngBlob, metadata);
      
      // Obter URL de download
      const downloadURL = await getDownloadURL(result.ref);

      // Atualizar o estado do negócio com a nova URL da imagem
      if (business) {
        setBusiness({
          ...business,
          image: downloadURL
        });
      }

      // Fechar o modal de upload
      setIsPhotoUploadModalOpen(false);
    } catch (error) {
      console.error('Erro no upload:', error);
      setUploadError(error instanceof Error ? error.message : 'Erro ao fazer upload da imagem');
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleImageUpload(file);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  if (!business) {
    return (
      <PageContainer>
        <div className="flex flex-col items-center justify-center h-[calc(100vh-4rem)]">
          <h1 className="text-2xl font-semibold text-white mb-4">Estabelecimento não encontrado</h1>
          <Link to="/" className="text-primary hover:text-primary/80">
            Voltar para a página inicial
          </Link>
        </div>
      </PageContainer>
    );
  }

  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, index) => (
      <span key={index}>
        {index < Math.floor(rating) ? (
          <StarIconSolid className="h-5 w-5 text-yellow-400 inline" />
        ) : (
          <StarIcon className="h-5 w-5 text-yellow-400 inline" />
        )}
      </span>
    ));
  };

  const header = (
    <div className="sticky top-0 z-10 bg-background pt-2 pb-4 px-4 border-b border-white/10">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-white">
            <ChevronLeftIcon className="h-6 w-6" />
          </Link>
          <h1 className="text-xl font-semibold text-white truncate">
            {business.name}
          </h1>
        </div>
        {user?.accessLevel && user.accessLevel >= 1 && (
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );

  const handleRateSubmit = async (rating: number) => {
    try {
      if (!user?.token) {
        throw new Error('User not authenticated');
      }

      let token = user.token;
      console.log('JWT Token for Rating:', {
        token: token,
        length: token.length,
        firstChars: token.substring(0, 10) + '...',
        lastChars: '...' + token.substring(token.length - 10)
      });
      
      let response = await fetch('https://us-central1-appdizquetem.cloudfunctions.net/rateBusiness', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          businessId: business.id,
          rating: rating
        })
      });

      // If token expired or server error, refresh and retry
      if (response.status === 401 || response.status === 500) {
        const newToken = await refreshToken();
        if (!newToken) {
          handleSessionExpired();
          return;
        }
        
        response = await fetch('https://us-central1-appdizquetem.cloudfunctions.net/rateBusiness', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${newToken}`
          },
          body: JSON.stringify({
            businessId: business.id,
            rating: rating
          })
        });
      }

      if (!response.ok) {
        if (response.status === 401 || response.status === 500) {
          handleSessionExpired();
          return;
        }
        throw new Error('Failed to submit rating');
      }
      
      // Show animation and play sound
      setShowConfetti(true);
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(error => {
          console.error('Error playing sound:', error);
        });
      }
      
      // Refetch business data to update the rating
      const updatedResponse = await fetch(`https://getbusinessdetail-b7jrylvg3a-uc.a.run.app?id=${id}`);
      const updatedBusinessData = await updatedResponse.json();
      
      const transformedBusiness: Business = {
        id: updatedBusinessData.id,
        name: updatedBusinessData.name,
        image: updatedBusinessData.image,
        rating: updatedBusinessData.stars,
        reviews: updatedBusinessData.reviews,
        description: updatedBusinessData.description,
        address: updatedBusinessData.address,
        category: updatedBusinessData.category,
        phone: updatedBusinessData.phone,
        premiumType: updatedBusinessData.premiumType,
        accessLevel: updatedBusinessData.accessLevel,
        instagram: updatedBusinessData.instagram,
      };
      
      setBusiness(transformedBusiness);
    } catch (error) {
      console.error('Error submitting rating:', error);
      if (error instanceof Error && (error.message === 'User not authenticated' || error.message.includes('Failed to submit rating'))) {
        handleSessionExpired();
      }
    }
  };

  const handleFavorite = async () => {
    try {
      if (!user?.token) {
        throw new Error('User not authenticated');
      }

      let token = user.token;
      console.log('JWT Token for Favorite:', {
        token: token,
        length: token.length,
        firstChars: token.substring(0, 10) + '...',
        lastChars: '...' + token.substring(token.length - 10)
      });

      const endpoint = isFavorite 
        ? 'https://us-central1-appdizquetem.cloudfunctions.net/removeFavorite'
        : 'https://us-central1-appdizquetem.cloudfunctions.net/addFavorite';

      let response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          businessId: business.id
        })
      });

      // If token expired or server error, refresh and retry
      if (response.status === 401 || response.status === 500) {
        const newToken = await refreshToken();
        if (!newToken) {
          handleSessionExpired();
          return;
        }
        
        response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${newToken}`
          },
          body: JSON.stringify({
            businessId: business.id
          })
        });
      }

      if (!response.ok) {
        if (response.status === 401 || response.status === 500) {
          handleSessionExpired();
          return;
        }
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${isFavorite ? 'remove' : 'add'} favorite`);
      }

      // Toggle the favorite status immediately
      setIsFavorite(!isFavorite);
      // Then check the actual status from the server
      await checkIfFavorited();
    } catch (error) {
      console.error('Error managing favorite:', error);
      if (error instanceof Error && (error.message === 'User not authenticated' || error.message.includes('Failed to'))) {
        handleSessionExpired();
      }
    }
  };

  const handleSaveBusiness = async (updatedBusiness: Business) => {
    try {
      if (!user?.token) {
        throw new Error('User not authenticated');
      }

      console.log('JWT Token for Save Business:', {
        token: user.token,
        length: user.token.length,
        firstChars: user.token.substring(0, 10) + '...',
        lastChars: '...' + user.token.substring(user.token.length - 10)
      });

      const response = await fetch('https://us-central1-appdizquetem.cloudfunctions.net/updateBusinessDetail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          businessId: business?.id,
          ...updatedBusiness
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update business');
      }

      // Update local state with the new business data
      setBusiness(updatedBusiness);
    } catch (error) {
      console.error('Error updating business:', error);
      throw error;
    }
  };

  return (
    <PageContainer stickyHeader={header}>
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-[9999]">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <Lottie
            animationData={starsAnimation}
            loop={false}
            style={{ width: '100%', height: '100%' }}
            onComplete={() => {
              setShowConfetti(false);
              if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
              }
            }}
            autoplay={true}
          />
        </div>
      )}
      <div className="space-y-6 overflow-hidden">
        {/* Image */}
        <div className="relative aspect-[16/9] rounded-lg overflow-hidden bg-white/5">
          {!loadedImages[business.image] && (
            <div className="absolute inset-0">
              <ShimmerEffect />
            </div>
          )}
          <img
            src={imageCache[business.image]?.src || business.image}
            alt={business.name}
            className={`w-full h-full object-cover ${!loadedImages[business.image] ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
            onLoad={() => handleImageLoad(business.image)}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              const fallbackSrc = `https://getimage-b7jrylvg3a-uc.a.run.app/categories/${business.category}.png`;
              if (fallbackCache[business.category]) {
                target.src = fallbackCache[business.category].src;
              } else {
                target.src = fallbackSrc;
              }
            }}
          />
          {(business.premiumType === 'gold' || business.premiumType === 'silver') && (
            <div className="absolute top-4 right-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm px-3 py-1 rounded-full">
              Destaque
            </div>
          )}
          {user?.accessLevel && user.accessLevel >= 1 && (
            <button
              onClick={() => setIsPhotoUploadModalOpen(true)}
              className="absolute bottom-4 right-4 p-2 rounded-lg bg-black/50 text-white hover:bg-black/70 transition-colors"
            >
              <PhotoIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button 
            className="flex-1 btn-secondary flex items-center justify-center gap-2 min-w-0"
            onClick={handleShare}
          >
            <ShareIcon className="h-5 w-5 flex-shrink-0" />
            <span className="truncate">Compartilhar</span>
          </button>
          <button 
            className="flex-1 btn-primary flex items-center justify-center gap-2 min-w-0"
            onClick={() => setIsRateModalOpen(true)}
          >
            <ChatBubbleLeftIcon className="h-5 w-5 flex-shrink-0" />
            <span className="truncate">Avaliar</span>
          </button>
          <motion.button
            className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors flex-shrink-0"
            onClick={handleFavorite}
            whileTap={{ scale: 0.9 }}
            animate={{ scale: isFavorite ? 1.1 : 1 }}
          >
            {isFavorite ? (
              <HeartIconSolid className="h-6 w-6 text-red-500" />
            ) : (
              <HeartIcon className="h-6 w-6" />
            )}
          </motion.button>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-2">
          {renderStars(business.rating/business.reviews)}
          <span className="text-white/80">{business.rating === 0 ? '0' : (business.rating/business.reviews).toFixed(1)}</span>
          <span className="text-white/80">
            ({business.reviews} avaliações)
          </span>
        </div>

        {/* Contact Info */}
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-white mb-1">
                {business.name}
              </h1>
              <div className="grid grid-cols-7 gap-2">
                {['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'].map((day, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <div
                      className={`w-full h-8 rounded-lg flex items-center justify-center text-sm font-medium
                        ${index > 0 && index < 6 ? 'bg-primary text-white' : 'bg-white/10 text-white/60'}`}
                    >
                      {day}
                    </div>
                    <span className="text-xs text-white/60 mt-1 text-center w-full">
                      {index > 0 && index < 6 ? '09:00 18:00' : (
                        <span className="flex flex-col items-center">
                          <span>Não</span>
                          <span>Abre</span>
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
            </div>
          </div>

          <div className="flex items-center gap-2 text-white/60">
            <MapPinIcon className="h-5 w-5" />
            <span>{business.address}</span>
          </div>

          <div className="flex items-center gap-2 text-white/60">
            <PhoneIcon className="h-5 w-5" />
            <span>{business.phone}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <a
            href={`https://wa.me/${business.phone.replace(/\D/g, '')}?text=${encodeURIComponent('Olá, vi seu negócio no App DizQueTem e gostaria de saber mais sobre seus produtos e serviços')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg text-center transition-colors flex items-center justify-center gap-2"
          >
            <img src={whatsappIcon} alt="WhatsApp" className="h-5 w-5" />
            WhatsApp
          </a>
          <a
            href={`https://instagram.com/${business.instagram || business.name.toLowerCase().replace(/\s+/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 text-white py-2 px-4 rounded-lg text-center transition-colors flex items-center justify-center gap-2"
          >
            <img src={instagramIcon} alt="Instagram" className="h-5 w-5" />
            Instagram
          </a>
        </div>
      </div>

      <RateModal
        isOpen={isRateModalOpen}
        onClose={() => setIsRateModalOpen(false)}
        businessName={business.name}
        onSubmit={handleRateSubmit}
      />

      {business && (
        <BusinessEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          business={business}
          onSave={handleSaveBusiness}
        />
      )}

      <SessionExpiredModal
        isOpen={isSessionExpiredModalOpen}
        onClose={() => setIsSessionExpiredModalOpen(false)}
      />

      {/* Modal de Upload de Foto */}
      <Dialog
        open={isPhotoUploadModalOpen}
        onClose={() => {
          setIsPhotoUploadModalOpen(false);
          setUploadError(null);
          setUploadProgress(0);
          setPreviewUrl(null);
        }}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md w-full bg-background rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <Dialog.Title className="text-xl font-semibold text-white">
                Upload de Foto
              </Dialog.Title>
              <button
                onClick={() => {
                  setIsPhotoUploadModalOpen(false);
                  setUploadError(null);
                  setUploadProgress(0);
                  setPreviewUrl(null);
                }}
                className="text-white/60 hover:text-white"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Preview Box */}
              <div className="relative aspect-[16/9] rounded-lg overflow-hidden bg-white/5">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                    <PhotoIcon className="h-12 w-12 text-white/40" />
                    <span className="text-white/40 text-sm">Selecione uma imagem</span>
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFileChange}
                  className="hidden"
                  id="photo-upload"
                />
                <label
                  htmlFor="photo-upload"
                  className="flex-1 cursor-pointer bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-center transition-colors"
                >
                  {previewUrl ? 'Alterar Imagem' : 'Selecionar Imagem'}
                </label>
              </div>

              {/* Progress Bar */}
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              )}

              {/* Error Message */}
              {uploadError && (
                <p className="text-sm text-red-500">{uploadError}</p>
              )}

              {/* Help Text */}
              <p className="text-sm text-white/60">
                Formatos aceitos: JPEG, PNG, GIF, WEBP
                <br />
                Tamanho máximo: 5MB
              </p>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
      />
    </PageContainer>
  );
};

export default BusinessDetail; 